import React, { useState, useEffect, useContext } from "react"
import SecuredByPP from "../../assets/images/securedByPayPal.svg"
import useLoading from "../../hooks/useLoading"
import { useLocation } from "react-router-dom"
import { Button } from "../ui/button"
import { ClientPaymentRequests } from "../../api/app.service"
import { SvgShieldFill, SvgWarningSign, SvgRedWarningSign } from "../icons"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

type Props = {}

const syncAccountSteps = []

const PaymentsSetupInstructions: React.FC<Props> = ({}: Props) => {
  const { user } = useContext(UserCtx)
  const { startLoading, stopLoading } = useLoading()
  const { toast } = useToast()
  const [paypalStatus, setPaypalStatus] = useState<any>(null)
  const query = new URLSearchParams(useLocation().search)
  const merchantIdInPayPal = query.get("merchantIdInPayPal")
  const navigate = useNavigate()
  const subscriptionOptions = () => navigate("/coach/subscription/plans")

  useEffect(() => {
    getPaypalOnboardingStatus()
  }, [])

  const getPaypalOnboardingStatus = async () => {
    if (user) {
      startLoading()
      try {
        const onboardingStatus =
          await ClientPaymentRequests.getPaypalOnboardingStatus({
            seller_payer_id: merchantIdInPayPal,
            seller_user_id: parseInt(user?.user_id),
          })
        setPaypalStatus(onboardingStatus)
      } catch (error) {
        toast.error(
          "An error occurred trying to fetch your PayPal onboarding status"
        )
      } finally {
        stopLoading()
      }
    }
  }

  const getPaypalOnboardingLink = async (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (user) {
        startLoading()
        ClientPaymentRequests.getPaypalOnboardingLink({
          user_id: user.user_id,
        })
          .then((data) => {
            const links = data.links
            if (links && links.length > 1) {
              resolve(links[1].href)
            } else {
              toast.error("Cannot find PayPal Link")
              reject("Cannot find PayPal Link")
            }
          })
          .catch((ex) => {
            toast.error(ex.response.data.message)
            reject(ex.response.data.message)
          })
          .finally(() => {
            stopLoading()
          })
      } else {
        reject("User not found")
      }
    })
  }

  const handleSignUpClick = async () => {
    try {
      const paypalLink = await getPaypalOnboardingLink()
      if (paypalLink) {
        window.open(paypalLink, "_self")
      }
    } catch (error) {
      console.error("Error getting PayPal link:", error)
    }
  }

  const handleDisconnectClick = async () => {
    try {
      const response =
        await ClientPaymentRequests.deletePaypalOnboardingAccount({
          seller_payer_id: merchantIdInPayPal,
          seller_user_id: parseInt(user.user_id),
        })
      alert("Disconnecting PayPal will disable all transactions")
      toast.success(`You have successfully disconnect PayPal account`)
      window.location.href = "/coach/client-payments"
    } catch (error) {
      console.error(error)
      toast.error(`An error occurred while disconnecting PayPal account`)
    }
  }

  const paypalAccountConncted = paypalStatus?.data.merchant_id
  const emailVerified = paypalStatus?.data.primary_email_confirmed
  const payPalOnboardingStatus = paypalStatus?.data?.onboarding_completed
  const paymentsReceivable = paypalStatus?.data?.payments_receivable

  // TODO: Do not erase the below commented block(s)

  // const paypalProtectionLinks = [
  //   {
  //     href: "https://intercom.help/zoee/en/articles/8623117-how-does-zoee-protect-your-information-on-paypal",
  //     text: "Zoee protects your payment information",
  //   },
  //   {
  //     href: "https://intercom.help/zoee/en/articles/8187172-how-do-i-get-paid-in-zoee#h_112090f95a",
  //     text: "fees",
  //   },
  // ]

  // const renderLink = ({ href, text }: { href: string; text: string }) => (
  //   <a
  //     href={href}
  //     target="_blank"
  //     rel="noopener noreferrer"
  //     className="text-blurple"
  //   >
  //     {text}
  //   </a>
  // )

  return (
    <>
      <div className="box-shadow-1 flex min-h-[234px] flex-col justify-between rounded-[16px] bg-white px-[16px] py-[32px] md:p-[40px]">
        <div className="mb-[40px] flex w-full flex-col justify-between md:flex-row md:items-center">
          <div className="flex flex-col gap-[8px] md:mb-0">
            <h6 className="text-[22px] font-bold">Getting paid on Zoee</h6>
            {/* <p className="text-graySlate text-[16px]">
              Learn more about how {renderLink(paypalProtectionLinks[0])} and{" "}
              {renderLink(paypalProtectionLinks[1])}.
            </p> */}
          </div>
        </div>
        <div className="bg-grayCloud flex w-full flex-col gap-[24px] rounded-[16px] px-[8px] py-[24px] md:gap-[32px] md:px-[40px] md:py-[40px]">
          <div className="flex flex-col items-center justify-between gap-[8px] md:flex-row md:gap-[24px]">
            {syncAccountSteps.length === 0 && (
              <div
                className="card-hover-shadow relative flex min-h-[180px] w-full flex-col items-center
                            justify-between gap-4 rounded-[12px] bg-white px-10 py-8"
              >
                <img
                  src={SecuredByPP}
                  alt="paypal secured"
                  className="w-[159px]"
                />
                <div className="flex flex-col items-center">
                  <div className="flex justify-center items-center mb-2 gap-1">
                    {paypalAccountConncted && !emailVerified && (
                      <SvgWarningSign className="mr-2" />
                    )}
                    {paypalAccountConncted && !paymentsReceivable && (
                      <SvgRedWarningSign className="mr-2" />
                    )}
                    {payPalOnboardingStatus &&
                      emailVerified &&
                      paymentsReceivable && <SvgShieldFill className="mr-1" />}
                    <h3 className="text-center text-[18px] font-bold ">
                      {!paypalAccountConncted &&
                        "Connect your PayPal account to get paid"}
                      {paypalAccountConncted &&
                        !emailVerified &&
                        "Verify PayPal email"}
                      {paypalAccountConncted &&
                        !paymentsReceivable &&
                        "Cannot receive payments"}
                      {payPalOnboardingStatus &&
                        emailVerified &&
                        paymentsReceivable &&
                        "Verified"}
                    </h3>
                  </div>
                  {!paypalAccountConncted && (
                    <>
                      {user.isSubscribed ? (
                        <div className="flex flex-col items-center">
                          <p className="text-center mb-4">
                            If your account isn’t able to connect, please try
                            again in 1–3 days. Otherwise, you may reach out to{" "}
                            <b>PayPal Customer Support</b>.
                          </p>
                          <Button
                            onClick={handleSignUpClick}
                            variant="default"
                            size="lg"
                          >
                            Connect a PayPal Account
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <p className="text-center mb-4">
                            Subscribe to Zoee to start getting paid for your
                            coaching services.
                          </p>
                          <Button onClick={subscriptionOptions} size="lg">
                            View Subscription Options
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                  {paypalAccountConncted && !emailVerified && (
                    <>
                      <p className="text-[16px] text-center">
                        Please confirm your email address on{" "}
                        <Button
                          variant="link"
                          size="sm"
                          onClick={handleSignUpClick}
                          className="underline p-0 text-[16px]"
                        >
                          PayPal Business settings
                        </Button>{" "}
                        in order to activate services!
                      </p>
                      <p className="text-[16px] text-center">
                        You currently cannot receive payments.
                      </p>
                    </>
                  )}
                  {paypalAccountConncted && !paymentsReceivable && (
                    <>
                      <p className="text-[16px] text-center mb-4">
                        You currently cannot receive payments due to possible
                        restriction on your PayPal account. Make sure your bank
                        account is connected in your{" "}
                        <a
                          href="https://www.paypal.com/myaccount"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline p-0 text-[16px]"
                        >
                          PayPal Account
                        </a>
                        .
                      </p>
                      <p>
                        {" "}
                        If issue persists, please reach out to{" "}
                        <b>Paypal Customer Support</b>
                      </p>
                    </>
                  )}
                  {payPalOnboardingStatus &&
                    emailVerified &&
                    paymentsReceivable && (
                      <>
                        <p className="mb-6 text-[16px] text-center">
                          Your verified email will be securely used for payouts.
                        </p>
                        <Button
                          variant="destructive"
                          size="lg"
                          onClick={handleDisconnectClick}
                        >
                          Disconnect PayPal
                        </Button>
                      </>
                    )}
                </div>
              </div>
            )}
          </div>
        </div>

        {!paypalAccountConncted && (
          <div className="mt-8 bg-grayCloud rounded-[16px] p-[16px] md:p-[40px]">
            <h3 className="text-[18px] font-bold mb-[16px]">
              Frequently Asked Questions
            </h3>
            <p className="text-[16px] mb-[24px]">
              Having trouble setting up PayPal? These are some common questions
              to help you troubleshoot.
            </p>
            <div className="flex flex-col gap-[16px]">
              <div>
                <h4 className="font-bold text-[16px] mb-[8px]">
                  1. What if I don’t want to connect PayPal?
                </h4>
                <p className="text-[16px]">
                  PayPal is Zoee’s secure payment processor, and all coaches are
                  required to connect a PayPal account to receive payments.
                  Clients can still pay with debit or credit cards, but PayPal
                  facilitates these transactions securely in the background.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-[16px] mb-[8px]">
                  2. I connected my PayPal account, but I still can’t create
                  services. Why?
                </h4>
                <p className="text-[16px]">
                  Your PayPal email might not be verified. Log in to your PayPal
                  dashboard and confirm your email to activate your account for
                  payments.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-[16px] mb-[8px]">
                  3. Can I use an existing PayPal account?
                </h4>
                <p className="text-[16px]">
                  Yes, you can use an existing PayPal account if it’s a business
                  account. If your account is personal, you can convert it into
                  a business account in PayPal settings.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-[16px] mb-[8px]">
                  4. I connected and verified my email, but I still can’t create
                  a service. What’s the issue?
                </h4>
                <p className="text-[16px]">
                  If your PayPal account is new, it may take 3–5 business days
                  for PayPal to verify your account details.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-[16px] mb-[8px]">
                  5. Why can’t I receive payments?
                </h4>
                <p className="text-[16px]">
                  This could be due to restrictions on your PayPal account. Make
                  sure your bank account is connected in your
                  <a
                    href="https://www.paypal.com/myaccount"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline ml-1"
                  >
                    PayPal Account Settings.
                  </a>{" "}
                  If the issue persists, contact PayPal Customer Support for
                  assistance.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default PaymentsSetupInstructions
